import { DomainError } from "./errors";
import { isValidGtin } from "./product";

/**
 * PDV (T3.1, RF-VEN-05): etiqueta de balança, GTIN, formas de pagamento com troco,
 * limite de desconto, conferência do caixa e hora da venda offline. Funções puras.
 */

export const POS_MAX_PAYMENTS = 10;
/** Venda offline pode chegar até 7 dias depois; relógio do terminal até 5 min adiantado. */
export const POS_MAX_PAST_MS = 7 * 24 * 3_600_000;
export const POS_MAX_FUTURE_MS = 5 * 60_000;

const isMoney = (v: number) => Number.isSafeInteger(v) && v > 0;

// ---------------------------------------------------------------- balança

export type ScaleValueKind = "price" | "weight";
export interface ScaleConfig {
  /** "2" ou "20".."29": prefixo de uso interno do EAN-13. */
  prefix: string;
  /** Dígitos do código do produto (4 a 6). */
  codeLength: number;
  /** O que vem embutido: preço em centavos ou peso em gramas. */
  valueKind: ScaleValueKind;
}

export const DEFAULT_SCALE_CONFIG: ScaleConfig = { prefix: "2", codeLength: 5, valueKind: "price" };

/** Valida campo a campo; o que vier inválido assume o padrão. */
export function normalizeScaleConfig(raw: unknown): ScaleConfig {
  const r = (typeof raw === "object" && raw !== null ? raw : {}) as Record<string, unknown>;
  const prefix =
    typeof r.prefix === "string" && /^2\d?$/.test(r.prefix) ? r.prefix : DEFAULT_SCALE_CONFIG.prefix;
  const codeLength =
    typeof r.codeLength === "number" &&
    Number.isInteger(r.codeLength) &&
    r.codeLength >= 4 &&
    r.codeLength <= 6
      ? r.codeLength
      : DEFAULT_SCALE_CONFIG.codeLength;
  const valueKind =
    r.valueKind === "price" || r.valueKind === "weight" ? r.valueKind : DEFAULT_SCALE_CONFIG.valueKind;
  return { prefix, codeLength, valueKind };
}

export interface ScaleLabel {
  /** Código do produto sem zeros à esquerda (casa com o SKU numérico). */
  productCode: string;
  valueKind: ScaleValueKind;
  priceCents: number | null;
  /** Peso em q4 de kg (gramas × 10). */
  weightQ4: number | null;
}

/** Lê a etiqueta: 13 dígitos, DV válido, prefixo configurado; valor ocupa o que sobra até o DV. */
export function parseScaleLabel(code: string, cfg: ScaleConfig): ScaleLabel | null {
  if (!/^\d{13}$/.test(code) || !isValidGtin(code) || !code.startsWith(cfg.prefix)) return null;
  const start = cfg.prefix.length;
  const productCode = code.slice(start, start + cfg.codeLength).replace(/^0+/, "");
  if (!productCode) return null;
  const value = Number(code.slice(start + cfg.codeLength, 12));
  return cfg.valueKind === "price"
    ? { productCode, valueKind: "price", priceCents: value, weightQ4: null }
    : { productCode, valueKind: "weight", priceCents: null, weightQ4: value * 10 };
}

/** SKU só com dígitos, comparado sem zeros à esquerda. */
export function scaleCodeMatches(sku: string | null, productCode: string): boolean {
  if (!sku || !/^\d+$/.test(sku)) return false;
  return sku.replace(/^0+/, "") === productCode;
}

/** Quantidade (q4) que dá o preço da etiqueta ao preço unitário (meio para cima). */
export function qtyForLabelPrice(labelCents: number, unitPriceCents: number): number {
  if (!isMoney(unitPriceCents))
    throw new DomainError("invalid_price", "Produto sem preço para etiqueta de balança.");
  if (!isMoney(labelCents)) throw new DomainError("invalid_qty", "Etiqueta sem valor.");
  const num = BigInt(labelCents) * 10_000n;
  const den = BigInt(unitPriceCents);
  return Number((num * 2n + den) / (den * 2n));
}

// ---------------------------------------------------------------- pagamentos

export type PosPaymentKind = "cash" | "pix" | "card" | "credit" | "other";
export const POS_PAYMENT_KINDS: readonly PosPaymentKind[] = ["cash", "pix", "card", "credit", "other"];

/** Classifica a forma de pagamento pelo tPag da SEFAZ. */
export function posPaymentKind(tPag: string | null | undefined): PosPaymentKind {
  switch (tPag) {
    case "01":
      return "cash";
    case "17":
    case "20":
      return "pix";
    case "03":
    case "04":
      return "card";
    case "05":
      return "credit";
    default:
      return "other";
  }
}

export interface PosPaymentIn {
  kind: PosPaymentKind;
  amountCents: number;
}

function checkPayments(totalCents: number, payments: PosPaymentIn[]): number {
  if (!isMoney(totalCents)) throw new DomainError("validation", "Venda sem valor.");
  if (payments.length === 0 || payments.length > POS_MAX_PAYMENTS)
    throw new DomainError("validation", `Informe de 1 a ${POS_MAX_PAYMENTS} pagamentos.`);
  let paid = 0;
  for (const p of payments) {
    if (!isMoney(p.amountCents)) throw new DomainError("invalid_money", "Valor de pagamento inválido.");
    paid += p.amountCents;
  }
  return paid;
}

/** Reduz `excess` dos pagamentos indicados, do último para o primeiro. */
function shave(
  applied: number[],
  payments: PosPaymentIn[],
  excess: number,
  pick: (p: PosPaymentIn) => boolean,
) {
  let left = excess;
  for (let k = payments.length - 1; k >= 0 && left > 0; k--) {
    if (!pick(payments[k]!)) continue;
    const cut = Math.min(applied[k]!, left);
    applied[k]! -= cut;
    left -= cut;
  }
  return left;
}

export interface PosAllocation {
  paidCents: number;
  changeCents: number;
  /** Valor efetivamente recebido em cada pagamento (o troco sai do dinheiro). */
  applied: number[];
}

/** Venda online: o pago cobre o total; o excedente é troco e só pode sair do dinheiro. */
export function allocatePosPayments(totalCents: number, payments: PosPaymentIn[]): PosAllocation {
  const paid = checkPayments(totalCents, payments);
  if (paid < totalCents)
    throw new DomainError("payment_insufficient", "Pagamento menor que o total da venda.");
  const changeCents = paid - totalCents;
  const applied = payments.map((p) => p.amountCents);
  if (shave(applied, payments, changeCents, (p) => p.kind === "cash") > 0)
    throw new DomainError("payment_exceeds", "Troco só pode sair de pagamento em dinheiro.");
  return { paidCents: paid, changeCents, applied };
}

export interface OfflineReconcile {
  applied: number[];
  /** O cliente pagou menos que o total recalculado: vira título de diferença. */
  shortfallCents: number;
  /** O cliente pagou mais: reduz o dinheiro e depois as demais formas. */
  surplusCents: number;
}

/** Venda offline já aconteceu: o servidor não recusa; ajusta pelo total recalculado. */
export function reconcileOfflinePayments(totalCents: number, payments: PosPaymentIn[]): OfflineReconcile {
  const paid = checkPayments(totalCents, payments);
  const applied = payments.map((p) => p.amountCents);
  if (paid <= totalCents) return { applied, shortfallCents: totalCents - paid, surplusCents: 0 };
  const surplus = paid - totalCents;
  const left = shave(applied, payments, surplus, (p) => p.kind === "cash");
  shave(applied, payments, left, (p) => p.kind !== "cash");
  return { applied, shortfallCents: 0, surplusCents: surplus };
}

// ---------------------------------------------------------------- desconto

/** Desconto efetivo em pct4 (1% = 10.000), truncado. */
export function effectiveDiscountPct4(grossCents: number, discountCents: number): number {
  if (grossCents <= 0) return 0;
  return Number((BigInt(discountCents) * 1_000_000n) / BigInt(grossCents));
}

/** Compara sem arredondar: desconto/bruto ≤ limite/100%. */
export function assertDiscountWithin(grossCents: number, discountCents: number, limitPct4: number): void {
  if (BigInt(discountCents) * 1_000_000n > BigInt(limitPct4) * BigInt(grossCents))
    throw new DomainError("discount_limit", "Desconto acima do limite permitido para o seu perfil.");
}

// ---------------------------------------------------------------- caixa

export type CashMovementKind = "sale" | "change" | "supply" | "withdrawal" | "refund" | "adjustment";
export interface CashMovementIn {
  kind: CashMovementKind;
  method: PosPaymentKind;
  /** Com sinal: entradas positivas, saídas negativas. */
  amountCents: number;
}
export type CashByMethod = Record<PosPaymentKind, number>;

/** Esperado por forma: fundo (no dinheiro) + movimentos; o ajuste do fechamento não entra. */
export function cashExpected(openingCents: number, movements: CashMovementIn[]): CashByMethod {
  const out: CashByMethod = { cash: openingCents, pix: 0, card: 0, credit: 0, other: 0 };
  for (const m of movements) if (m.kind !== "adjustment") out[m.method] += m.amountCents;
  return out;
}

export interface CashCloseLine {
  method: PosPaymentKind;
  expectedCents: number;
  countedCents: number;
  differenceCents: number;
}

/** Fechamento cego: o operador informa o contado; a diferença é contado − esperado. */
export function closeCash(expected: CashByMethod, counted: Partial<CashByMethod>) {
  const lines: CashCloseLine[] = [];
  let total = 0;
  for (const method of POS_PAYMENT_KINDS) {
    const c = counted[method];
    if (c !== undefined && (!Number.isSafeInteger(c) || c < 0))
      throw new DomainError("invalid_money", "Valor contado inválido.");
    const exp = expected[method];
    if (method !== "cash" && exp === 0 && c === undefined) continue;
    const countedCents = c ?? 0;
    lines.push({ method, expectedCents: exp, countedCents, differenceCents: countedCents - exp });
    total += countedCents - exp;
  }
  return { lines, differenceCents: total, cashDifferenceCents: lines[0]!.differenceCents };
}

// ---------------------------------------------------------------- hora e contingência

/** Hora informada pelo terminal: até 5 min no futuro e 7 dias no passado. */
export function checkSaleTime(iso: string, now: Date): Date {
  const t = new Date(iso);
  const ms = t.getTime();
  if (Number.isNaN(ms) || ms > now.getTime() + POS_MAX_FUTURE_MS || ms < now.getTime() - POS_MAX_PAST_MS)
    throw new DomainError("invalid_sale_time", "Hora da venda fora do intervalo aceito.");
  return t;
}

/** Prazo para transmitir a NFC-e emitida em contingência. */
export function contingencyDeadline(contingencyAtIso: string, hours: number): Date {
  return new Date(new Date(contingencyAtIso).getTime() + hours * 3_600_000);
}
